import { FormattedMessage, useIntl } from 'react-intl';
import { ListItem, Stack, Typography, useTheme } from '@mui/material';
import React, { FC, MouseEventHandler, useEffect, useState } from 'react';
import {
  RecentSearchModel,
  recentSearchUrl,
  useAddRecentSearchResult,
  useGetRecentSearchResults,
  useGetSearchResults,
} from '@/logic/services';
import { useAuth, useDialog, useMovieFinder, useSnack } from '@/logic/state';

import Autocomplete from '@mui/material/Autocomplete';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CustomizedCircularProgress from '@/components/shared/customized-circular-progress';

import { QueryKeyType } from '@/logic/libs';
import { SxProps } from '@mui/system';
import TextField from '@mui/material/TextField';
import debounce from 'lodash/debounce';

import { pxToRem } from '@/theme/typography';

import shallow from 'zustand/shallow';
import uniq from 'lodash/uniq';
import { useCity } from '@/logic/hooks';
import { useQueryClient } from '@tanstack/react-query';
import ImageBoxFuture from '../shared/image-box-future';

const NoResultsFound = '/icons/movie-finder/no-results-icon.svg';
const NotAllowedIcon = '/icons/shared/not-allowed-icon.svg';
const historyIcon = '/icons/shared/time-circle.svg';
const searchLocalIcon = '/icons/shared/search.svg';

const SEARCH_TERM_MIN_LENGTH = 3;

export enum Searchable {
  CINEMA = 'CINEMA',
  EXPERIENCE = 'EXPERIENCE',
  FILM = 'FILM',
}

const SearchOption = ({ item, isHistory, scrollToItem, ...props }) => {
  const theme = useTheme();
  const setSnack = useSnack(s => s.setSnack, shallow);
  const intl = useIntl();

  const chipStyle: SxProps = {
    minWidth: { tablet: pxToRem(59), mobile: '59px' },
    minHeight: { tablet: pxToRem(32), mobile: '32px' },
    padding: '5px 10px',
    backgroundColor: '#C4F6FD',
    color: theme.palette.secondary.main,
    mx: { mobile: '12px', tablet: pxToRem(12) },
    fontSize: { mobile: '13px', tablet: pxToRem(13) },
    display: 'flex',
    borderRadius: '30px',
    justifyContent: 'center',
    alignItems: 'center',
  };

  const handleClick = (e): void => {
    if (item.isDisabled) {
      setSnack({
        isOpen: true,
        message: intl.formatMessage({
          id: 'movie_finder.not_available_option',
        }),
        icon: (
          <ImageBoxFuture
            img={NotAllowedIcon}
            title={intl.formatMessage({
              id: 'alt.not_allowed_icon',
            })}
            objectFit='contain'
            sx={{
              width: '16px',
              height: '16px',
            }}
          />
        ),
        type: 'info',
      });

      return;
    }

    scrollToItem(item.searchableType, item.searchableId);
    props?.onClick?.(e);
  };

  return (
    <ListItem
      {...props}
      sx={{
        fontSize: { tablet: pxToRem(16), mobile: '16px' },
      }}
      onClick={handleClick}
    >
      <ImageBoxFuture
        img={isHistory ? historyIcon : searchLocalIcon}
        title={intl.formatMessage({
          id: isHistory ? 'alt.history_icon' : 'alt.search_icon',
        })}
        objectFit='contain'
        sx={{
          mr: '8px',
          width: { tablet: pxToRem(24), mobile: '24px' },
          height: { tablet: pxToRem(24), mobile: '24px' },
        }}
      />

      <Typography
        component='span'
        sx={{
          fontSize: {
            mobile: '13px',
            tablet: pxToRem(13),
          },
        }}
      >
        {item.name}
      </Typography>
      <Typography
        component='span'
        sx={{
          ...chipStyle,
          backgroundColor: '#C4F6FD',
          color: theme.palette.secondary.main,
        }}
      >
        <FormattedMessage id={`movie.finder.${item.searchableType.toLowerCase()}`} />
      </Typography>
      {item.isDisabled && (
        <Typography
          component='span'
          sx={{
            ...chipStyle,

            backgroundColor: theme.palette.lightGrey.main,
            color: theme.palette.grey[40],
          }}
        >
          <FormattedMessage
            id={
              item.isDisabled
                ? 'movie_finder.search.not_available'
                : 'movie_finder.search.available'
            }
          />
        </Typography>
      )}
    </ListItem>
  );
};

interface SearchProps {
  scrollToItem: (type: Searchable, id: number) => void;
}

export const Search: FC<SearchProps> = ({ scrollToItem }) => {
  const { cityId } = useCity();
  const user = useAuth(s => s.user, shallow);

  const [termState, setTermState] = useState<string>('');
  const [debouncedTermState, setDebouncedTermState] = useState<string>('');

  const queryClient = useQueryClient();

  const [
    selectedDate,
    selectedTimeDuration,
    setSelectedMovies,
    selectedCinemas,
    setSelectedCinemas,
    selectedExperiences,
    setSelectedExperiences,
    setSearchText,
  ] = useMovieFinder(
    s => [
      s.selectedDate,
      s.selectedTimeDuration,
      s.setSelectedMovies,
      s.selectedCinemas,
      s.setSelectedCinemas,
      s.selectedExperiences,
      s.setSelectedExperiences,
      s.setSearchText,
    ],
    shallow
  );

  useEffect(() => {
    const trackTermState = debounce(() => {
      setDebouncedTermState(termState);
      setSearchText(termState);
    }, 500);
    trackTermState();

    return () => trackTermState.cancel();
  }, [setSearchText, termState]);

  const theme = useTheme();
  const intl = useIntl();
  const setDialog = useDialog(s => s.setDialog, shallow);

  const addRecentSearchResult = useAddRecentSearchResult(
    {},
    {
      onSuccess: () => {
        queryClient.invalidateQueries({
          predicate: query => {
            return (query.queryKey[0] as QueryKeyType)?.url === recentSearchUrl;
          },
        });
      },
    }
  );
  const { data: recentSearches, isLoading: isLoadingRecent } = useGetRecentSearchResults(
    {
      params: {
        cityId,
        date: selectedDate,
        timeDuration: selectedTimeDuration,
      },
    },
    { enabled: !!selectedDate && !!user?.id }
  );

  const { data: searchesData, isFetching: isLoadingSearch } = useGetSearchResults(
    {
      params: {
        cityId,
        date: selectedDate,
        timeDuration: selectedTimeDuration,
        term: debouncedTermState,
        experienceIds: selectedExperiences,
        cinemaIds: selectedCinemas,
      },
    },
    {
      enabled: !!selectedDate && debouncedTermState.length >= SEARCH_TERM_MIN_LENGTH,
    }
  );

  const selectSearchResult = ({
    searchableId,
    searchableType,
  }: RecentSearchModel): void => {
    if (user?.id) {
      addRecentSearchResult.safeMutateAsync({
        data: {
          searchableId,
          searchableType,
        },
      });
    }

    switch (searchableType) {
      case Searchable.CINEMA:
        setSelectedCinemas(prevValues => uniq([...prevValues, searchableId]));
        break;
      case Searchable.EXPERIENCE:
        setSelectedExperiences(prevValues => uniq([...prevValues, searchableId]));
        break;
      case Searchable.FILM:
        setSelectedMovies(prevValues => uniq([...prevValues, searchableId]));
        break;
    }
  };

  const isLoading = isLoadingRecent || isLoadingSearch;

  const isShowHistory =
    user?.id && (!termState || termState?.length < SEARCH_TERM_MIN_LENGTH);

  const options = (isShowHistory ? recentSearches?.data : searchesData?.data) || [];
  const [isOpen, setIsOpen] = useState(false);
  const handleOptionChange = (_, item: RecentSearchModel) => {
    if (!item) return;
    selectSearchResult(item);
  };

  const handleConfirmClearHistory: MouseEventHandler<HTMLButtonElement> = e => {
    e.stopPropagation();

    setDialog({
      wrapper: true,
      isClearHistoryConfirmDialog: true,
    });
  };

  return (
    <Box
      sx={{
        '.MuiPaper-root': {
          borderRadius: '12px !important',
        },
        '.MuiAutocomplete-popper': {
          top: '5px !important',
        },
        display: 'flex',
        marginBottom: {
          mobile: '50px',
          tablet: '100px',
        },
      }}
    >
      <Autocomplete
        id='movie-finder-search'
        openOnFocus
        open={isOpen}
        onOpen={() => setIsOpen(true)}
        onClose={() => setIsOpen(false)}
        onChange={handleOptionChange}
        freeSolo={false}
        options={options}
        getOptionLabel={(option: RecentSearchModel) => option.name}
        filterOptions={item => item}
        disablePortal={true}
        inputValue={termState}
        onInputChange={(_, newInputValue) => {
          setTermState(newInputValue);
        }}
        loading={isLoading}
        loadingText={intl.formatMessage({ id: 'movie_finder.search.loading' })}
        sx={{
          width: '100%',
          maxWidth: '650px',
          '.MuiAutocomplete-inputRoot': {
            borderRadius: '12px',
            backgroundColor: 'white',
          },
        }}
        renderOption={(props, item) => (
          <SearchOption
            item={item}
            isHistory={isShowHistory}
            scrollToItem={scrollToItem}
            {...props}
          />
        )}
        renderInput={params => (
          <TextField
            {...params}
            placeholder={intl.formatMessage({ id: 'movie_finder.search' })}
            value={termState}
            InputProps={{
              ...params.InputProps,
              startAdornment: (
                <ImageBoxFuture
                  img={searchLocalIcon}
                  title={intl.formatMessage({
                    id: 'alt.search_icon',
                  })}
                  objectFit='contain'
                  sx={{
                    mr: '8px',
                    width: { tablet: pxToRem(14), mobile: '14px' },
                    height: { tablet: pxToRem(14), mobile: '14px' },
                  }}
                />
              ),
              endAdornment: (
                <>
                  {isLoadingSearch ? (
                    <CustomizedCircularProgress size={20} thickness={4} />
                  ) : null}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
          />
        )}
        ListboxComponent={props => {
          return (
            <Box {...props}>
              {isShowHistory && (
                <Stack
                  direction='row'
                  alignItems='center'
                  justifyContent='space-between'
                  sx={{
                    marginBottom: 1,
                    marginTop: 0.5,
                    paddingX: '16px',
                  }}
                >
                  <Typography
                    sx={{
                      color: theme.palette.grey[90],
                      fontWeight: 700,
                      fontSize: pxToRem(18),
                    }}
                  >
                    <FormattedMessage id='movie_finder.search.recent_searches' />
                  </Typography>

                  <Button
                    sx={{
                      color: theme.palette.grey[50],
                      fontSize: pxToRem(14),
                      minWidth: 'fit-content',
                    }}
                    onClick={handleConfirmClearHistory}
                  >
                    <FormattedMessage id='movie_finder.search.clear_recent' />
                  </Button>
                </Stack>
              )}
              <>{props.children}</>
            </Box>
          );
        }}
        noOptionsText={
          searchesData?.data?.length === 0 &&
          termState?.length >= SEARCH_TERM_MIN_LENGTH ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
              <ImageBoxFuture
                img={NoResultsFound}
                title={intl.formatMessage({
                  id: 'alt.not_allowed_icon',
                })}
                objectFit='contain'
                sx={{
                  width: '72px',
                  height: '72px',
                }}
              />
              <Typography
                sx={{
                  textAlign: 'center',
                  fontWeight: '400',
                  fontSize: pxToRem(16),
                  mt: pxToRem(24),
                  width: '50%',
                }}
              >
                <FormattedMessage id='movie.finder.no.results.found' />
              </Typography>
            </Box>
          ) : (
            <Box>
              <Typography
                sx={{
                  fontWeight: '400',
                  fontSize: pxToRem(16),
                  // mt: pxToRem(24),
                  width: '50%',
                }}
              >
                <FormattedMessage id='movie.finder.search.hint' />
              </Typography>
            </Box>
          )
        }
      />
    </Box>
  );
};
